// Eskom PARCC — merge a transaction's AuditLog entries and CES snapshots into one
// time-ordered timeline (PURE: no React, no Firebase).
//
// A CES capture writes its audit row (useCaptureCes step 5) BEFORE its
// EvaluationSheetBackup snapshot (step 6), both for the same transaction. So an
// audit `ces_capture`/`ces_upload` and the snapshot that follows it within a breath
// are ONE event, collapsed here into a single row that carries the snapshot's
// "View captured sheet" affordance. Both orphan cases are real and handled, not
// assumed away: an orphan snapshot (the audit write failed) renders alone; an
// orphan capture audit (the best-effort snapshot insert failed) renders with no
// affordance.
import type { AuditLogEntry, SnapshotSummary } from '../../lib/db';
import type { FieldChange } from '../../lib/fieldDiff';

/** One merged timeline row: an audit entry, a CES snapshot, or a collapsed pair. */
export interface HistoryRow {
  /** Stable React key. */
  id: string;
  /** The event timestamp (the audit's `at`, or an orphan snapshot's `createdAt`). */
  at: string;
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  note: string | null;
  changes: FieldChange[];
  /** Present when the row has a viewable captured sheet (a pair OR an orphan snapshot). */
  snapshot?: {
    id: string;
    /** The next-older snapshot's id (for the flyout diff), or null for the oldest. */
    previousId: string | null;
    reviewStatus: string | null;
    sourceAction: string | null;
  };
}

const CAPTURE_ACTIONS = new Set(['ces_capture', 'ces_upload']);
const TOLERANCE_MS = 5000;

/** |a − b| in ms between two ISO timestamps, or Infinity if either is unparseable. */
function gapMs(a: string, b: string): number {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isNaN(ta) || Number.isNaN(tb) ? Infinity : Math.abs(ta - tb);
}

/**
 * The nearest capture audit (`ces_capture`/`ces_upload`) within ±5s of a snapshot's
 * `createdAt`, or null. The ±5s tolerance is a judgement call (the snapshot lands a
 * beat after its audit), isolated here so it is easy to tune.
 */
export function findCaptureEntry(
  snapshot: SnapshotSummary,
  entries: readonly AuditLogEntry[],
): AuditLogEntry | null {
  let best: AuditLogEntry | null = null;
  let bestGap = Infinity;
  for (const e of entries) {
    if (!CAPTURE_ACTIONS.has(e.action)) continue;
    const g = gapMs(e.at, snapshot.createdAt);
    if (g <= TOLERANCE_MS && g < bestGap) {
      best = e;
      bestGap = g;
    }
  }
  return best;
}

/**
 * Who captured a snapshot — JOINED from the nearest capture audit (±5s), never read
 * from the snapshot (EvaluationSheetBackup.reviewer is never written, so every
 * existing snapshot's reviewer is null). Null when no capture audit is near enough.
 */
export function joinSnapshotActor(
  snapshot: SnapshotSummary,
  entries: readonly AuditLogEntry[],
): string | null {
  return findCaptureEntry(snapshot, entries)?.actorEmail ?? null;
}

/**
 * Merge audit entries + CES snapshots into one DESC (newest-first) timeline. At an
 * equal timestamp an audit-bearing row leads its snapshot-only sibling, preserving
 * the real causal order of a capture (audit written before snapshot).
 */
export function mergeHistory(
  entries: readonly AuditLogEntry[],
  snapshots: readonly SnapshotSummary[],
): HistoryRow[] {
  // Snapshots newest-first, each carrying the previous (next-older) snapshot's id —
  // computed ONCE here so the flyout never re-derives it.
  const snapsDesc = [...snapshots].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const previousIdOf = new Map<string, string | null>();
  snapsDesc.forEach((s, i) => previousIdOf.set(s.id, snapsDesc[i + 1]?.id ?? null));

  const consumed = new Set<string>(); // audit ids collapsed into a snapshot pair
  // `rank` (audit-bearing 0, orphan-snapshot 1) is tracked ALONGSIDE the row, not
  // on it, so the returned HistoryRow carries no sort-only field.
  const items: { row: HistoryRow; rank: number }[] = [];

  // One row per snapshot: a collapsed capture-pair when a capture audit is near, or
  // an orphan snapshot (audit write failed) rendered alone.
  for (const s of snapsDesc) {
    const entry = findCaptureEntry(s, entries);
    if (entry) consumed.add(entry.id);
    items.push({
      rank: entry ? 0 : 1,
      row: {
        id: entry ? entry.id : `snap-${s.id}`,
        at: entry ? entry.at : s.createdAt,
        actorEmail: entry ? entry.actorEmail : null,
        action: entry ? entry.action : (s.sourceAction ?? 'ces_capture'),
        entityType: entry ? entry.entityType : 'Transaction',
        note: entry ? entry.note : null,
        changes: entry ? entry.changes : [],
        snapshot: {
          id: s.id,
          previousId: previousIdOf.get(s.id) ?? null,
          reviewStatus: s.reviewStatus,
          sourceAction: s.sourceAction,
        },
      },
    });
  }

  // Every audit entry not collapsed into a snapshot — including an orphan capture
  // (its best-effort snapshot insert failed), which renders with no affordance.
  for (const e of entries) {
    if (consumed.has(e.id)) continue;
    items.push({
      rank: 0,
      row: {
        id: e.id,
        at: e.at,
        actorEmail: e.actorEmail,
        action: e.action,
        entityType: e.entityType,
        note: e.note,
        changes: e.changes,
      },
    });
  }

  items.sort((a, b) => {
    const ta = Date.parse(a.row.at);
    const tb = Date.parse(b.row.at);
    if (tb !== ta) return tb - ta; // newest first
    return a.rank - b.rank; // audit before snapshot at ties
  });
  return items.map((i) => i.row);
}
